/*
    This is a rather complex flow for handling the CW API Connection validation. Basically, there's the following steps:
    1.  The user fills out the form and submits.
    2.  The form is validated (with the hidden field connection_validated set to "False").
    3a. If the validation fails, the validation errors are shown.
    3b. If it passes but connection_validated is False, the server does *nothing*! On the client, a modal is opened and an async API check call is made.
    4a. If the API check comes back negative, the 'Save' button is not enabled and the user has to cancel.
    4b. If it comes back positive, the 'Save' button is enabled.
    5.  Once the user clicks on 'Save', we set the hidden field connection_validated to "True" and submit the *same form* again.
    6.  By means of the field connection_validated, the server this time knows we're serious and actually stores the form when it passes validation. (The case that it *now* not passes is unexpected and has no defined/tested behavior!)
    7.  The form is reset (specifically: connection_validated is set back to "False"!) to allow the flow to start over.
*/

define(['jquery',
        'core/urls',
        'core/utilities'
    ],
    function($, urls, utilities) {

        let nodes = {
            header: document.querySelector("h2.clickable"),
            statusHeader: document.querySelector(".status-wrapper h2"),
            statusRefreshWrap: document.querySelector(".status-wrapper h2 .refresh-wrap"),
            statusRefreshIcon: document.querySelector(".status-wrapper h2 .refresh-wrap img"),
            apiForm: document.querySelector(".form-wrapper form"),
            inputs: {
                siteUrl: document.querySelector("#id_site_url"),
                companyId: document.querySelector("#id_company_id"),
                publicKey: document.querySelector("#id_api_public_key"),
                privateKey: document.querySelector("#id_api_private_key"),
                cloudRadialToggle: document.querySelector("#id_enable_cloud_radial"),
                cloudRadialPublicKey: document.querySelector("#id_cloud_radial_public_key"),
                cloudRadialPrivateKey: document.querySelector("#id_cloud_radial_private_key"),
                siteUrlError: document.querySelector("#error_site_url"),
                companyIdError: document.querySelector("#error_company_id"),
                publicKeyError: document.querySelector("#error_api_public_key"),
                privateKeyError: document.querySelector("#error_api_private_key"),
                cloudRadialToggleError: document.querySelector("#error_enable_cloud_radial"),
                cloudRadialPublicKeyError: document.querySelector("#error_cloud_radial_public_key"),
                cloudRadialPrivateKeyError: document.querySelector("#error_cloud_radial_private_key"),
            },
            statusWrapper: document.querySelector(".status-wrapper"),
            statusWrapperTitle: document.querySelector(".status-wrapper .title"),
            statusWrapperContent: document.querySelector(".status-wrapper .content"),
            allSectionsStatus: document.querySelector(".status-wrapper .overall-status"),
            apiResultsLoading: document.querySelector(".status-wrapper .results .loading"),
            errorState: document.querySelector(".status-wrapper .results .error-state"),
            progressBar: document.querySelector(".status-wrapper .results .loading .progress-bar"),
            apiResults: document.querySelector(".status-wrapper .results .success"),
            continueButton: document.querySelector(".status-wrapper .button-controls.continue"),
            cloudRadialToggle: document.querySelector("#id_enable_cloud_radial"),
            cloudRadialWrapper: document.querySelector(".cloud-radial-wrapper"),
            saveButton: document.querySelector(".authentication-form .submit"),
            copyButton: document.querySelector("#copy_hook"),
        }

        let requests = 0;
        let taskId = '';
        let loadingIntervalID;
        let formData;

        let stopLoading = () => {
            nodes.saveButton.classList.remove("disabled");
            clearInterval(loadingIntervalID);
        }

        let allSectionsStatus;

        // storing permissions in object so we can control order of rendering
        let sections = {
            'cm': {
                title: "Companies",
                output: '',
                success_count: 0,
                total_count: 0,
                status: "passed",
                error_msg: `SmileBack CSAT, NPS, Project surveying and reporting features may not work as expected. Please update SmileBack 
                            security <a href="https://docs.connectwise.com/SmileBack/040/020/030" target="_blank">role permissions</a> within ConnectWise PSA.`
            },
            'ag': {
                title: "Finance",
                output: '',
                success_count: 0,
                total_count: 0,
                status: "passed",
                error_msg: `You won't be able to use Agreement Types as an additional filter option in your NPS Campaigns. 
                            Please update SmileBack security <a href="https://docs.connectwise.com/SmileBack/040/020/030" target="_blank">role permissions</a> within ConnectWise PSA.`
            },
            'mk': {
                title: "Marketing",
                output: '',
                success_count: 0,
                total_count: 0,
                status: "passed",
                error_msg: `You won't be able to use Marketing Groups as an additional filter option in your NPS Campaigns. 
                            Please update SmileBack security <a href="https://docs.connectwise.com/SmileBack/040/020/030" target="_blank">role permissions</a> within ConnectWise PSA.`
            },
            'pm': {
                title: "Project",
                output: '',
                success_count: 0,
                total_count: 0,
                status: "passed",
                error_msg: `SmileBack Project Surveys may not work as expected. 
                            Please update SmileBack security <a href="https://docs.connectwise.com/SmileBack/040/020/030" target="_blank">role permissions</a> within ConnectWise PSA.`
            },
            'sr': {
                title: "Service Desk",
                output: '',
                success_count: 0,
                total_count: 0,
                status: "passed",
                error_msg: `SmileBack core CSAT features, such as Ticket data fetching and Automation Rules may not work as expected.  
                            Please check SmileBack security <a href="https://docs.connectwise.com/SmileBack/040/020/030" target="_blank">role permissions</a> within ConnectWise PSA.`
            },
            'sa': {
                title: "System",
                output: '',
                success_count: 0,
                total_count: 0,
                status: "passed",
                error_msg: `SmileBack API Member, core CSAT, NPS, and Project features, such as Project and Phase status
                            fetching, may not work as expected. Please check SmileBack security <a href="https://docs.connectwise.com/SmileBack/040/020/030" target="_blank">role permissions</a> within ConnectWise PSA.`
            },
            'te': {
                title: "Time & Expense",
                output: '',
                success_count: 0,
                total_count: 0,
                status: "passed",
                error_msg: `SmileBack core CSAT features, such as Ticket Resources fetching, may not work as expected. 
                            Please check SmileBack security <a href="https://docs.connectwise.com/SmileBack/040/020/030" target="_blank">role permissions</a> within ConnectWise PSA.`
            },
        }

        let addCloudRadialSection = () => {
            sections.cloud_radial = {
                title: "Cloud Radial",
                output: '',
                success_count: 0,
                total_count: 0,
                status: "passed",
                error_msg: `SmileBack CloudRadial integration may not work as expected. Please 
                            check CloudRadial credentials and try again.`
            }
        }

        let validationConfig = [];

        let buildValidationConfig = () => {
            validationConfig = [
                {
                    input: nodes.inputs.siteUrl,
                    errorNode: nodes.inputs.siteUrlError,
                    validations: ["hasValue"],
                },
                {
                    input: nodes.inputs.companyId,
                    errorNode: nodes.inputs.companyIdError,
                    validations: ["hasValue"],
                },
                {
                    input: nodes.inputs.publicKey,
                    errorNode: nodes.inputs.publicKeyError,
                    validations: ["hasValue"],
                },
                
            ];

            if(!nodes.inputs.privateKey.placeholder.length) {
                validationConfig.push(
                    {
                        input: nodes.inputs.privateKey,
                        errorNode: nodes.inputs.privateKeyError,
                        validations: ["hasValue"],
                    },
                )
            }

            if(nodes.inputs.cloudRadialToggle.checked) {
                validationConfig.push(
                    {
                        input: nodes.inputs.cloudRadialPublicKey,
                        errorNode: nodes.inputs.cloudRadialPublicKeyError,
                        validations: ["hasValue"],
                    }
                )

                if(!nodes.inputs.cloudRadialPrivateKey.placeholder.length) {
                    validationConfig.push(
                        {
                            input: nodes.inputs.cloudRadialPrivateKey,
                            errorNode: nodes.inputs.cloudRadialPrivateKeyError,
                            validations: ["hasValue"],
                        },
                    )
                }

                addCloudRadialSection();
            } else {
                delete sections.cloud_radial;
            }
        }

        let renderChildPermissions = (data) => {
            let output = '<div class="child-permissions">';
            let groupStatus = 'passed';

            data.forEach(permission => {
                let status = "passed";
                if(permission.isAllowed !== true) {
                    status = "failed";
                    groupStatus = "failed";
                    allSectionsStatus.text = "Issues found";
                    allSectionsStatus.class = "fail";
                }

                output += `<div class="child-permission permission ${status}">
                                <div class="title">${permission.name}</div>
                            </div>`
            })

            output += '</div>';

            return {
                output,
                groupStatus
            };
        }

        let renderPermissions = (data, id) => {
            
            let output = '';
            let successCount = data.length;
            sections[id].status = "passed";
            
            data.forEach(permission => {
                let statusOutput = "";
                let status = "passed"; 

                if(permission.fails.length) {
                    if(sections[id]) sections[id].status = "failed";
                    status = "failed";
                    statusOutput = `<div class="status">`;
                    successCount--;
                    allSectionsStatus.text = "Issues found";
                    allSectionsStatus.class = "fail";

                    permission.fails.forEach(fail => {
                        statusOutput += `<div class="fail">
                                        <strong>${fail.level}</strong> 
                                        value is <strong>${fail.actual}</strong>. 
                                        Expected: <strong>${fail.expected}</strong>
                                    </div>`
                    })
                    statusOutput += `</div>`
                }

                let childPermissions = '';

                if(permission.childPermission) {
                    // render child permissions if any
                    let childPermissionOutput = renderChildPermissions(permission.childPermission);
                    childPermissions = childPermissionOutput.output;
                    
                    // show group as failed if any child permissions fail
                    if(childPermissionOutput.groupStatus === 'failed') {
                        sections[id].status = "failed";
                    }
                }

                output += `<div class="permission ${status}">
                                <div class="title">${permission.description}</div>
                                ${childPermissions}
                                ${statusOutput}
                            </div>`
            })

            if(sections[id]) {
                sections[id].total_count = data.length;
                sections[id].success_count = successCount;
            }

            return output;
        }

        let renderResults = (response) => { 

            allSectionsStatus = {
                text: "All checks passed",
                class: "success",
            }
            
            let error = "";

            nodes.continueButton.classList.add("hide");

            if(response.data.connectivity !== "pass") {
                error = "We encountered an API permissions error. Please check the API Member, and Security Role.";

                switch(response.data.connectivity) {
                    case 'invalid_credentials':
                        error = "We encountered a problem with API credentials. Please check Site, Company ID, and Keys.";
                        break;
                    case 'invalid_cw_instance':
                        error = "We encountered a problem with API credentials. Please check Site, Company ID, and Keys.";
                        break;
                    case 'general_failure':
                        error = "We encountered a problem with API credentials. Please check Site, Company ID, and Keys.";
                        break;
                    case 'ssl_failure':
                        error = "We encountered an SSL connection problem your site. Please make sure that hosting server has valid SSL certificate and supports latest TLS protocol.";
                        break;
                    case 'host_unreachable':
                        error = "We encountered a problem reaching your site. Please check Site URL and make sure our App requests to it are not blocked.";
                        break;
                }

                nodes.errorState.innerHTML = `${error}`;

                if(sections.cloud_radial && !response.data.cloud_radial_access) {
                    nodes.errorState.innerHTML += "<br><hr style='width:100%'>Communication with CloudRadial failed. Please re-check keys."
                }
                
                nodes.errorState.classList.remove('hide');
                nodes.apiResultsLoading.classList.add("hide");
                stopLoading();
                return;
            }

            if(nodes.header) {
                nodes.header.classList.remove("active");
                nodes.header.nextElementSibling.classList.add("hide");
            }

            let output = '';
            
            response.data.permissions.forEach((permission) => {
                let id = permission.id;
                
                if(sections[id]) {
                    let renderedPermissions = renderPermissions(permission.functions, id);
                    let status = "Pass";
                    let errorClass = "hide";
                    if(sections[id].status === "failed") {
                       status = "Fail";
                       errorClass = '';
                    }

                    console.log("SECTION", sections)

                    sections[id].output = `
                        <div class="permissions-group">
                            <div class="header ${sections[id].status}">
                                ${sections[id].title}
                                <div class="status">
                                    ${status} 
                                </div>
                            </div>
                            <div class='permissions'>
                                ${renderedPermissions}
                            </div>
                            <div class="error-msg ${errorClass}">${sections[id].error_msg}</div>
                        </div>
                    `;
                }
            })

            if(sections.cloud_radial) {
                let status = "Fail";
                let statusClass = "failed"; 
                let errorClass = "";
                
                if(response.data.cloud_radial_access) {
                    status = "Pass";
                    statusClass = "passed"; 
                    errorClass = "hide";
                }

                sections.cloud_radial.output = `
                    <h2>CloudRadial </h2>
                    <div class="permissions-group">
                        <div class="header ${statusClass}">
                            CloudRadial Integration
                            <div class="status">
                                ${status} 
                            </div>
                        </div>
                        <div class='permissions'>
                            <div class="permission ${statusClass}">
                                <div class="title">Connection Status</div>
                            </div>
                        </div>
                        <div class="error-msg ${errorClass}">${sections.cloud_radial.error_msg}</div>
                    </div>
                `;
            }

            Object.keys(sections).forEach(section => {
                output += sections[section].output;
            })

            nodes.allSectionsStatus.classList.remove("success");
            nodes.allSectionsStatus.classList.remove("fail");

            nodes.allSectionsStatus.innerHTML = allSectionsStatus.text;
            nodes.allSectionsStatus.classList.add(allSectionsStatus.class)
            nodes.apiResults.innerHTML = output;

            nodes.statusRefreshWrap.classList.remove("hide");
            
            if(nodes.apiResults.classList.contains("new-user")) {
                nodes.continueButton.classList.remove("hide");
            }
            
            nodes.apiResults.classList.remove("hide");
            nodes.apiResultsLoading.classList.add("hide");
            scrollToResults();
            stopLoading();
        }

        let showProgress = () => {
            nodes.progressBar.style.width = "10%";
            let percentage = 10;
            let loops = 0;

            loadingIntervalID = setInterval(() => {
                loops ++;
                percentage += Math.floor(Math.random() * 5) + 1;
                if(percentage >= 100) {
                    percentage = Math.floor(Math.random() * (60 - 10 + 1) + 10)
                }
                if(loops > 1000) {
                    stopLoading();
                }
                nodes.progressBar.style.width = `${percentage}%`;
            }, 500)
        }

        let generateTaskId = () => {
            formData = new FormData(nodes.apiForm);

            nodes.errorState.classList.add('hide');
            nodes.apiResults.classList.add("hide");
            nodes.apiResultsLoading.classList.remove("hide");
            nodes.allSectionsStatus.innerHTML = '';

            showProgress();

            fetch(urls['ticketsource:connectwise-save-validate:request'](), {
                method: 'POST', 
                body: formData
            })
            .then((response) => response.json())
            .then((data) => {
                if(data.immediate_result) {
                    let errors = data.immediate_result.errors;
                    nodes.errorState.innerHTML = '';
                    Object.keys(errors).forEach(key => {
                        nodes.errorState.innerHTML += errors[key][0].message + "<br>";
                    })
                    
                    nodes.errorState.classList.remove('hide');
                    nodes.apiResultsLoading.classList.add("hide");
                    stopLoading();
                    return;
                }

                taskId = data.tid;
                validateApiCredentials();
            })
            .catch((error) => {
                console.log("ERROR", error)
                nodes.errorState.innerText = "Something went wrong on our end. Please try again or contact tech support if issue persists.";
                nodes.errorState.classList.remove('hide');
                nodes.apiResultsLoading.classList.add("hide");
                stopLoading();
            });
        }

        let saveCloudRadialToggle = (value) => {
            fetch(urls['ticketsource:cloudradial-onof'](value), {
                method: 'GET'
            })
        }

        let validateApiCredentials = () => {
            fetch(urls['ticketsource:connectwise-save-validate:poll'](taskId), {
                method: 'GET'
            })
            .then((response) => response.json())
            .then((data) => {
                if(data.state !== "SUCCESS") {
                    requests++
                    if(requests < 15) {
                        console.log("pending")
                        setTimeout(validateApiCredentials, 2000)
                    } else {
                        console.log("timeout")
                        stopLoading();
                        nodes.errorState.innerHTML = "Something went wrong on our end. Please try again or contact tech support if issue persists."
                        nodes.errorState.classList.remove('hide');
                        nodes.apiResultsLoading.classList.add("hide");
                    }                    
                } else {
                    renderResults(data);
                }
            })
            .catch(err => {
                console.log("error", err)
                nodes.errorState.innerText = "Something went wrong on our end. Please try again or contact tech support if issue persists.";
                nodes.errorState.classList.remove('hide');
                nodes.apiResultsLoading.classList.add("hide");
                stopLoading();
            })
        }

        let copyWebHook = (e) => {
                $('input[name="cloud_radial_webhook"]').select();
                document.execCommand("copy");
                let restore = $('#copy_hook').text();
                $('#copy_hook').html('<i class="glyphicons glyphicons-ok"></i> Copied!');
                setTimeout(function () {
                    $('#copy_hook').text(restore);
                }, 1500);
        }

        let scrollToResults = () => {
            const yOffset = -10; 
            const position = nodes.statusWrapperTitle.getBoundingClientRect().top + window.pageYOffset + yOffset;

            window.scrollTo({top: position, behavior: 'smooth'});
        }

        let validateAndSave = () => {
            buildValidationConfig();
            let formIsValid = utilities.validateForm(validationConfig);

            if(formIsValid) {     
                nodes.saveButton.classList.add("disabled");           
                generateTaskId();
                nodes.statusWrapper.classList.remove("hide");

                if(!nodes.statusWrapperTitle.classList.contains("active")) {
                    nodes.statusWrapperTitle.classList.add("active");
                    nodes.statusWrapperContent.classList.remove("hide");
                }

                scrollToResults();
            }
        }

        let createEventListeners = () => {
            nodes.cloudRadialToggle.addEventListener("change", (e) => {
                let value = 0;
                if(e.target.checked) {
                    nodes.cloudRadialWrapper.classList.remove("hide");
                    value = 1;
                } else {
                    nodes.cloudRadialWrapper.classList.add("hide");
                }

                saveCloudRadialToggle(value);
            })

            if(nodes.header) {
                nodes.header.addEventListener("click", (e) => {
                    let content = e.target.nextElementSibling;
                    if(e.target.classList.contains("active")) {
                        e.target.classList.remove("active");
                        content.classList.add("hide");
                    } else {
                        e.target.classList.add("active");
                        content.classList.remove("hide");
                    }
                })
            }
            
            nodes.saveButton.addEventListener("click", (e) => {
                e.preventDefault();
                validateAndSave();
            })

            nodes.copyButton.addEventListener("click", () => {
                let url = document.querySelector("#id_cloud_radial_webhook").value;
                const type = "text/plain";
                const blob = new Blob([url], { type });
                const data = [new ClipboardItem({ [type]: blob })];

                navigator.clipboard.write(data).then(() => {
                        nodes.copyButton.innerHTML = "Copied!"

                        setTimeout(() => {
                            nodes.copyButton.innerHTML = "Copy";
                        }, 4000)
                    },
                );
            })

            nodes.statusRefreshWrap.addEventListener("click", () => {
                nodes.statusRefreshIcon.classList.add("roll");
                setTimeout(() => {
                    nodes.statusRefreshIcon.classList.remove("roll");
                    nodes.statusRefreshIcon.classList.add("unroll");
                }, 300)

                setTimeout(() => {
                    nodes.statusRefreshIcon.classList.remove("unroll");
                }, 400)
                validateAndSave();
            })
        }

        return {
            init: function() {
                if(nodes.apiForm.dataset.completed == "True") {
                    if(nodes.inputs.cloudRadialToggle.checked) {
                        nodes.cloudRadialWrapper.classList.remove("hide");
                        addCloudRadialSection();
                    }

                    generateTaskId();
                }
                createEventListeners();
            }
        };
    }
);
